import { matchPath } from 'react-router-dom';
import { ProjectSummary } from '../services/api';

export type WorkspaceSection =
  | 'projects'
  | 'workflow'
  | 'orders'
  | 'invoices'
  | 'profile';

export const PROJECT_STEP_PATHS = [
  'settings',
  'analysis',
  'outline',
  'content',
] as const;

export type ProjectStepPath = typeof PROJECT_STEP_PATHS[number];

export interface WorkspaceRoute {
  section: WorkspaceSection;
  projectId: number | null;
  step: number;
  isNewProject: boolean;
}

const parseProjectId = (value: string | undefined): number | null => {
  if (!value || !/^\d+$/.test(value)) {
    return null;
  }
  const projectId = Number(value);
  return Number.isSafeInteger(projectId) && projectId > 0 ? projectId : null;
};

export const parseWorkspaceRoute = (pathname: string): WorkspaceRoute => {
  if (pathname === '/orders') {
    return { section: 'orders', projectId: null, step: 0, isNewProject: false };
  }
  if (pathname === '/invoices') {
    return { section: 'invoices', projectId: null, step: 0, isNewProject: false };
  }
  if (pathname === '/profile/bind-wechat') {
    return { section: 'profile', projectId: null, step: 0, isNewProject: false };
  }
  if (pathname === '/projects/new') {
    return { section: 'workflow', projectId: null, step: 0, isNewProject: true };
  }

  const match = matchPath('/projects/:projectId/:step', pathname);
  const projectId = parseProjectId(match?.params.projectId);
  const stepPath = match?.params.step as ProjectStepPath | undefined;
  const step = stepPath ? PROJECT_STEP_PATHS.indexOf(stepPath) : -1;
  if (projectId && step >= 0) {
    return {
      section: 'workflow',
      projectId,
      step,
      isNewProject: false,
    };
  }

  return { section: 'projects', projectId: null, step: 0, isNewProject: false };
};

export const getProjectStepPath = (projectId: number, step: number): string => {
  const safeStep = Math.min(
    PROJECT_STEP_PATHS.length - 1,
    Math.max(0, step)
  );
  return `/projects/${projectId}/${PROJECT_STEP_PATHS[safeStep]}`;
};

export const getRecoverableProjectStep = (project: ProjectSummary): number => {
  if (project.flow_status >= 30) return 3;
  if (project.flow_status >= 20) return 2;
  if (project.flow_status >= 10) return 1;
  return 0;
};

export const getFlowStepForStatus = (flowStatus: number): number => {
  if (flowStatus >= 30) return 3;
  if (flowStatus >= 20) return 2;
  if (flowStatus >= 10) return 1;
  return 0;
};

export const FLOW_STATUS_LABELS: Record<number, string> = {
  0: '初始',
  10: '已上传文件',
  20: '已解析',
  25: '已生成编写思路',
  30: '已生成目录',
  40: '已生成正文',
};
